import React, { useCallback } from 'react';

import { Switch, Route } from 'react-router-dom';
import { useHistory, useParams, useRouteMatch } from 'react-router';
import { useHotkeys } from 'react-hotkeys-hook';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/pro-light-svg-icons';

import { Options } from './Options/Options';
import { Stakefactors } from './Stakefactors/Stakefactors';
import { Indicators } from './Indicators/Indicators';
import { OptionEvaluation } from './OptionEvaluation/OptionEvaluation';

import Loader from '../../components/Loading/Loader';
import ProjectModal from '../../components/Modal/ProjectModal';
import ProjectNavigation from '../../components/navigation/ProjectNavigation';
import Decision from './Decision/Decision';
import HeaderPortal from '../../layout/header/HeaderPortal';
import SubHeaderPortal from '../../layout/header/SubHeaderPortal';

import {
	useGetProjectQuery,
	useUpdateOptionEvaluationMutation,
	useUpdateProjectMutation,
} from '../../graphql/generated';

import './Project.sass';
import Results from './Results/Results';
import { Constraints } from './Constraints/Constraints';
import { useTranslation } from 'react-i18next';

export function Project(props: any) {
	let history = useHistory();
	const params: any = useParams();
	const { t } = useTranslation();
	const [editedItem, setEditedItem] = React.useState<any | null>(null);

	const [optionIndex, setOptionIndex] = React.useState<any | null>(0);

	const setRealOptionIndex = (index: number) => {
		let realIndex = index % (data?.getProject?.options?.length || 0);
		if (realIndex < 0) {
			realIndex = (data?.getProject?.options?.length || 0) - 1;
		}
		setOptionIndex(realIndex);
	}

	const [updateData] = useUpdateProjectMutation();
	const [updateOptionEvaluation] = useUpdateOptionEvaluationMutation();

	const { data, refetch, loading } = useGetProjectQuery({ variables: { id: params?.id } });
	const _refetch = useCallback(() => {
		setTimeout(() => refetch(), 0);
	}, [refetch]);

	// Change scenario with arrow keys
	useHotkeys('ArrowRight', () => setRealOptionIndex(optionIndex + 1));
	useHotkeys('ArrowLeft', () => setRealOptionIndex(optionIndex - 1));

	// Update project
	const handleUpdateProject = async (id: any, values: any) => {
		const result = await updateData({
			variables: {
				id: id,
				...values,
			},
		});

		await _refetch();
	};

	// Update stakefactors evaluation
	const handleUpdateStakefactorEvaluation = async (
		optionId: string,
		itemId: string,
		value: number,
		description: string
	) => {
		const result = await (updateOptionEvaluation)({
			variables: {
				optionId,
				itemId,
				value,
				description,
				kind: 'stakefactor',
			},
		});

		await _refetch();
	};

	const onGoNext = (url: string) => {
		history.push(url);
	};

	// Update indicators evaluation
	const handleUpdateIndicatorEvaluation = async (optionId: string, itemId: string, value: number, description: string) => {
		console.log('handleUpdateIndicatorEvaluation', optionId, itemId, value, description);
		const result = await (updateOptionEvaluation)({
			variables: {
				optionId,
				itemId,
				value,
				description,
				kind: 'indicator',
			},
		});

		await _refetch();
	};

	// Update indicators evaluation
	const handleUpdateConstraintEvaluation = async (
		optionId: string,
		itemId: string,
		value: number,
		description: string
	) => {
		console.log('handleUpdateConstraintEvaluation', optionId, itemId, value, description);

		const result = await (updateOptionEvaluation)({
			variables: {
				optionId,
				itemId,
				value,
				description,
				kind: 'constraint',
			},
		});

		await _refetch();
	};

	console.log(params, data);

	return (
		<div className="Project">
			<HeaderPortal>
				{data?.getProject?.name ? data?.getProject?.name : data?.getProject ? 'No name' : ''}

				<FontAwesomeIcon
					icon={faEdit}
					onClick={() => setEditedItem(data?.getProject)}
					style={{ marginLeft: 'auto' }}
				/>
			</HeaderPortal>
			<SubHeaderPortal>
				<ProjectNavigation></ProjectNavigation>
			</SubHeaderPortal>
			{loading ? (
				<Loader></Loader>
			) : (
				<>

					<Switch>
						<Route
							key="project"
							exact
							path="/projects/:id"
							render={() => (
								<Decision
									onGoNext={onGoNext}
									onEdit={() => setEditedItem(data?.getProject)}
									{...data?.getProject}
								></Decision>
							)}
						></Route>
						<Route
							key="project"
							path={`/projects/:id/${t("models.stakefactor.slug")}`}
							render={() => (
								<Stakefactors
									onGoNext={onGoNext}
									refetch={_refetch}
									{...data?.getProject}
									popModal={props.popModal}
									pushModal={props.pushModal}
								></Stakefactors>
							)}
						></Route>
						<Route
							key="project"
							path={`/projects/:id/${t("models.indicator.slug")}`}
							render={() => (
								<Indicators
									onGoNext={onGoNext}
									refetch={_refetch}
									{...data?.getProject}
									popModal={props.popModal}
									pushModal={props.pushModal}
								></Indicators>
							)}
						></Route>
						<Route
							key="project"
							path={`/projects/:id/${t("models.constraint.slug")}`}
							render={() => (
								<Constraints
									onGoNext={onGoNext}
									refetch={_refetch}
									{...data?.getProject}
									popModal={props.popModal}
									pushModal={props.pushModal}
								></Constraints>
							)}
						></Route>
						<Route
							key="project"
							path={`/projects/:id/${t("models.option.slug")}`}
							render={() => (
								<Options
									onGoNext={onGoNext}
									refetch={_refetch}
									{...data?.getProject}
									popModal={props.popModal}
									pushModal={props.pushModal}
								></Options>
							)}
						></Route>
						<Route
							key="project"
							path="/projects/:id/options-evaluation"

							render={() => (
								<OptionEvaluation
									optionIndex={optionIndex}
									setOptionIndex={setRealOptionIndex}
									onGoNext={onGoNext}
									onUpdateStakefactorEvaluation={handleUpdateStakefactorEvaluation}
									onUpdateIndicatorEvaluation={handleUpdateIndicatorEvaluation}
									onUpdateConstraintEvaluation={handleUpdateConstraintEvaluation}
									refetch={_refetch}
									{...data?.getProject}
									popModal={props.popModal}
									pushModal={props.pushModal}
								></OptionEvaluation>
							)}
						></Route>
						<Route
							key="project"
							path="/projects/:id/results"
							render={() => <Results
								optionIndex={optionIndex}
								setOptionIndex={setRealOptionIndex}
								refetch={_refetch}
								{...data?.getProject}
								popModal={props.popModal}
								pushModal={props.pushModal}
							></Results>}
						></Route>
					</Switch>
					{props.children}
				</>
			)
			}
			{
				editedItem && (
					<ProjectModal
						onHide={() => {
							setEditedItem(null);
						}}
						onSave={(values: Object) => {
							handleUpdateProject(editedItem.id, values);
							setEditedItem(null);
						}}
						id={editedItem?.id}
						values={{
							name: editedItem?.name,
							context: editedItem?.context,
							description: editedItem?.description,
							experienceLevel: editedItem?.experienceLevel,
						}}
					></ProjectModal>
				)
			}
		</div >
	);
}
